import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { HashRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AuthGate, SessionProvider, useCurrentUser } from "@/auth";
import { createQueryClient } from "@/lib/queryClient";
import { useOrgMembership } from "@/lib/useOrgMembership";
import { AppShell, ErrorBoundary, SkyBackdrop } from "@/shell";
import { OverlayProvider } from "@/ui/overlay/OverlayProvider";
import { MaterialAppProvider } from "@/ui/material/MaterialAppProvider";
import { consentIdFromPath } from "./features/oauth-consent/model";
import { canSeeRoute, defaultRoute, redirects, routePattern, routes } from "./routes";

const OAuthConsent = lazy(() => import("./features/oauth-consent"));

/**
 * Composition root.
 *
 * Provider order matters: the query client wraps the session (which invalidates
 * caches on sign-in and clears them on sign-out), the session wraps the router,
 * and the auth gate stands between the router and the shell so no feature ever
 * renders without a live connection and a valid token.
 *
 * `OverlayProvider` (tooltip → toast → peek) sits *inside* the gate and
 * *outside* the shell. Inside, because a signed-out screen has nothing to
 * announce; outside, because a toast has to survive the route change that
 * caused it — "Task deleted — Undo" must not disappear because deleting the
 * task moved you to another day — and because the peek outlives the surface
 * that opened it. Stacking is not set by this nesting: `LAYER` in
 * `ui/overlay/surface.ts` puts toasts above dialogs above the peek.
 *
 * `SkyBackdrop` sits inside the shell screen so the black rail and bezel stay
 * clear. LoginScreen mounts its own backdrop while signed out.
 *
 * The OAuth consent screen (`#/oauth/consent/<id>`, docs/oauth.md) is the one route
 * outside the shell: a page Claude sends the user to, with nothing else around it. It
 * is still inside the gate, so a signed-out visitor signs in and comes back to it.
 *
 * Hash routing keeps deep links working identically across the three surfaces
 * (Tauri asset protocol, static PWA hosting from `pb_public/`, dev server)
 * without needing a server-side SPA fallback.
 *
 * Route modules are lazy so Material UI and feature-only code do not force a
 * one-megabyte first download. `useTransitions={false}` remains explicit: the
 * selected navigation state commits immediately, while each route owns a calm
 * Material loading surface for its first visit.
 */

const queryClient = createQueryClient();

export default function App() {
  return (
    <MaterialAppProvider>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <HashRouter useTransitions={false}>
            <AuthGate>
              <SignedInSurface />
            </AuthGate>
          </HashRouter>
        </SessionProvider>
      </QueryClientProvider>
    </MaterialAppProvider>
  );
}

function SignedInSurface() {
  const consentId = consentIdFromPath(useLocation().pathname);
  if (consentId) {
    return (
      <Suspense fallback={null}>
        <OAuthConsent id={consentId} />
      </Suspense>
    );
  }
  return (
    <OverlayProvider>
      <AppShell backdrop={<SkyBackdrop />}>
        <FeatureRoutes />
      </AppShell>
    </OverlayProvider>
  );
}

function FeatureRoutes() {
  const location = useLocation();
  const email = useCurrentUser()?.email;
  const membership = useOrgMembership();
  // While membership is unknown an organisation page stays routed, so a deep link
  // is not bounced to the dashboard; the page itself waits for the answer.
  const visible = (route: (typeof routes)[number]) =>
    canSeeRoute(route, email, membership.isMember) || (route.orgOnly === true && membership.isPending && canSeeRoute(route, email, true));

  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Routes>
        <Route path="/" element={<Navigate to={`/${defaultRoute}`} replace />} />
        {routes.filter(visible).map((route) => (
          // The pattern carries the surface's optional trailing parameter, so
          // `#/tasks` and `#/tasks/2026-08-04` resolve to the same screen.
          <Route
            key={route.path}
            path={routePattern(route)}
            element={
              <Suspense fallback={<RouteLoading />}>
                <route.Component />
              </Suspense>
            }
          />
        ))}
        {Object.entries(redirects).map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={`/${to}`} replace />} />
        ))}
        <Route path="*" element={<Navigate to={`/${defaultRoute}`} replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

function RouteLoading() {
  return (
    <div role="status" aria-label="Loading page" className="flex min-h-56 flex-col gap-4 py-4">
      <span className="h-8 w-44 animate-pulse rounded-surface bg-fill-3" />
      <span className="h-24 w-full animate-pulse rounded-surface bg-surface" />
      <span className="h-40 w-full animate-pulse rounded-surface bg-surface" />
    </div>
  );
}
